import { Route, Routes } from '@angular/router';
import { LipoRouteEnum } from './shared/enums/lipo-route.enum';
import { LipoRoutesDataModel } from './shared/models/lipo-routes-data.model';
import { KeycloakRoleEnum } from './shared/enums/keycloak-role.enum';
import { routeRoleConfig } from './shared/routeRoleConfig';
import { canActivateAuthRole } from './keycloak/guards/auth.guard';
import { realmGuard } from './keycloak/guards/realm.guard';

const partnerRoute: Route = {
  path: LipoRouteEnum.PARTNER,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./partner/components/partner-overview/partner-overview.component').then(
          (m) => m.PartnerOverviewComponent,
        ),
      data: { roles: routeRoleConfig.partner } as LipoRoutesDataModel,
    },
    {
      path: 'new',
      loadComponent: () =>
        import('./partner/components/partner-detail/partner-detail.component').then(
          (m) => m.PartnerDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.partner,
      } as LipoRoutesDataModel,
    },
    {
      path: ':id',
      loadComponent: () =>
        import('./partner/components/partner-detail/partner-detail.component').then(
          (m) => m.PartnerDetailComponent,
        ),
      data: {
        isCreateItem: false,
        roles: routeRoleConfig.partner,
      } as LipoRoutesDataModel,
    },
  ],
};

const licenseRoute: Route = {
  path: LipoRouteEnum.LICENCE,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./licence/components/licence-overview/licence-overview.component').then(
          (m) => m.LicenceOverviewComponent,
        ),
      data: { roles: routeRoleConfig.license } as LipoRoutesDataModel,
    },
    {
      path: ':id/partner/access',
      loadComponent: () =>
        import('./licence/components/partner-license-access-detail/partner-license-access-detail.component').then(
          (m) => m.PartnerLicenseAccessDetailComponent,
        ),
      data: { roles: routeRoleConfig.license } as LipoRoutesDataModel,
    },
  ],
};

const userMappingRoute: Route = {
  path: LipoRouteEnum.USER_MAPPING,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./tenant/components/user-mapping-detail/tenant-user-mapping-detail.component').then(
          (m) => m.TenantUserMappingDetailComponent,
        ),
      data: { roles: routeRoleConfig.userMapping } as LipoRoutesDataModel,
    },
    {
      path: 'new',
      loadComponent: () =>
        import('./tenant/components/user-mapping-detail/tenant-user-mapping-detail.component').then(
          (m) => m.TenantUserMappingDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.userMapping,
      } as LipoRoutesDataModel,
    },
    {
      path: ':userMappingId',
      loadComponent: () =>
        import('./tenant/components/user-mapping-detail/tenant-user-mapping-detail.component').then(
          (m) => m.TenantUserMappingDetailComponent,
        ),
      data: {
        isCreateItem: false,
        roles: routeRoleConfig.userMapping,
      } as LipoRoutesDataModel,
    },
  ],
};

const tokenRoute: Route = {
  path: LipoRouteEnum.TOKEN,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./tenant/components/token-detail/tenant-token-detail.component').then(
          (m) => m.TenantTokenDetailComponent,
        ),
      data: { roles: routeRoleConfig.token } as LipoRoutesDataModel,
    },
    {
      path: 'new',
      loadComponent: () =>
        import('./tenant/components/token-detail/tenant-token-detail.component').then(
          (m) => m.TenantTokenDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.token,
      } as LipoRoutesDataModel,
    },
  ],
};

const tenantRoute: Route = {
  path: LipoRouteEnum.TENANT,
  children: [
    {
      path: 'new',
      loadComponent: () =>
        import('./tenant/components/tenant-detail/tenant-detail.component').then(
          (m) => m.TenantDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.tenant,
      } as LipoRoutesDataModel,
    },
    {
      path: ':tenantId',
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./tenant/components/tenant-detail/tenant-detail.component').then(
              (m) => m.TenantDetailComponent,
            ),
          data: {
            isCreateItem: false,
            roles: routeRoleConfig.tenant,
          } as LipoRoutesDataModel,
        },
        tokenRoute,
        userMappingRoute,
      ],
    },
  ],
};

const systemRoute: Route = {
  path: LipoRouteEnum.SYSTEM,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./system/components/system-overview/system-overview.component').then(
          (m) => m.SystemOverviewComponent,
        ),
      data: { roles: routeRoleConfig.system } as LipoRoutesDataModel,
    },
    {
      path: 'new',
      loadComponent: () =>
        import('./system/components/system-detail/system-detail.component').then(
          (m) => m.SystemDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.system,
      } as LipoRoutesDataModel,
    },
    {
      path: ':systemId',
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./system/components/system-detail/system-detail.component').then(
              (m) => m.SystemDetailComponent,
            ),
          data: {
            isCreateItem: false,
            roles: routeRoleConfig.system,
          } as LipoRoutesDataModel,
        },
        tenantRoute,
      ],
    },
  ],
};

const productRoute: Route = {
  path: LipoRouteEnum.PRODUCT,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./product/components/product-overview/product-overview.component').then(
          (m) => m.ProductOverviewComponent,
        ),
      data: {
        roles: [...routeRoleConfig.product, KeycloakRoleEnum.CUSTOMER],
      } as LipoRoutesDataModel,
    },
    {
      path: 'new',
      loadComponent: () =>
        import('./product/components/prodcut-detail/product-detail.component').then(
          (m) => m.ProductDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.product,
      } as LipoRoutesDataModel,
    },
    {
      path: ':productId',
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./product/components/prodcut-detail/product-detail.component').then(
              (m) => m.ProductDetailComponent,
            ),
          data: {
            isCreateItem: false,
            roles: [...routeRoleConfig.product, KeycloakRoleEnum.CUSTOMER],
          } as LipoRoutesDataModel,
        },
      ],
    },
  ],
};

const userRoute: Route = {
  path: LipoRouteEnum.USER,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./user/components/user-detail/user-detail.component').then(
          (m) => m.UserDetailComponent,
        ),
      data: { roles: routeRoleConfig.userRole } as LipoRoutesDataModel,
    },
    {
      path: 'new',
      loadComponent: () =>
        import('./user/components/user-detail/user-detail.component').then(
          (m) => m.UserDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.userRole,
      } as LipoRoutesDataModel,
    },
    {
      path: ':userId',
      loadComponent: () =>
        import('./user/components/user-detail/user-detail.component').then(
          (m) => m.UserDetailComponent,
        ),
      data: {
        isCreateItem: false,
        roles: routeRoleConfig.userRole,
      } as LipoRoutesDataModel,
    },
  ],
};

const wizardRoute: Route = {
  path: LipoRouteEnum.WIZARD,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./wizard/components/wizard-start/wizard-start.component').then(
          (m) => m.WizardStartComponent,
        ),
      data: { roles: routeRoleConfig.wizard } as LipoRoutesDataModel,
    },
    {
      path: 'processing/:jobId',
      loadComponent: () =>
        import('./wizard/components/wizard-processing/wizard-processing.component').then(
          (m) => m.WizardProcessingComponent,
        ),
      data: { roles: routeRoleConfig.wizard } as LipoRoutesDataModel,
    },
  ],
};

const proxyRoute: Route = {
  path: LipoRouteEnum.PROXY,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./proxy/components/proxy-overview/proxy-overview.component').then(
          (m) => m.ProxyOverviewComponent,
        ),
      data: { roles: routeRoleConfig.proxy } as LipoRoutesDataModel,
    },
  ],
};

const customerRoute: Route = {
  path: LipoRouteEnum.CUSTOMER,
  children: [
    {
      path: '',
      loadComponent: () =>
        import('./customer/components/customer-overview/customer-overview.component').then(
          (m) => m.CustomerOverviewComponent,
        ),
      data: { roles: routeRoleConfig.customer } as LipoRoutesDataModel,
    },
    {
      path: 'new',
      loadComponent: () =>
        import('./customer/components/customer-detail/customer-detail.component').then(
          (m) => m.CustomerDetailComponent,
        ),
      data: {
        isCreateItem: true,
        roles: routeRoleConfig.customer,
      } as LipoRoutesDataModel,
    },
    {
      path: ':customerId',
      children: [
        {
          path: '',
          loadComponent: () =>
            import('./customer/components/customer-detail/customer-detail.component').then(
              (m) => m.CustomerDetailComponent,
            ),
          data: {
            isCreateItem: false,
            roles: routeRoleConfig.customer,
          } as LipoRoutesDataModel,
        },
        userRoute,
      ],
    },
  ],
};

export const routes: Routes = [
  {
    path: LipoRouteEnum.AUTH,
    loadComponent: () => import('./layouts/login/login.component').then((m) => m.LoginComponent),
    children: [
      {
        path: LipoRouteEnum.REALM_CHOOSER,
        loadComponent: () =>
          import('./keycloak/realmChooser/realm-chooser.component').then(
            (m) => m.RealmChooserComponent,
          ),
        canActivate: [realmGuard],
      },
    ],
  },
  {
    path: LipoRouteEnum.NOT_ALLOWED,
    loadComponent: () =>
      import('./keycloak/not-allowed/not-allowed.component').then((m) => m.NotAllowedComponent),
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/default/default.component').then((m) => m.DefaultComponent),
    canActivateChild: [canActivateAuthRole],
    children: [
      {
        path: LipoRouteEnum.HOME,
        loadComponent: () =>
          import('./shared/components/lipo-landing/lipo-landing.component').then(
            (m) => m.LipoLandingComponent,
          ),
      },
      partnerRoute,
      customerRoute,
      systemRoute,
      productRoute,
      licenseRoute,
      wizardRoute,
      proxyRoute,
    ],
  },
];
